// Vendor Purchase History — listing table.
// Columns/patterns mirror src/Components/orders/BillingTable.tsx (Invoice ID,
// Created On/By formatting). Edit-only row action, per spec. Current/Previous
// Year Purchase, Purchase Growth and the Purchase History drill-in now live
// on the Vendor module's own list (VendorTable.tsx) instead — they were
// vendor-level aggregates that repeated identically across every one of a
// vendor's rows here, which was redundant. Text search is gone too, replaced
// by a Vendor (searchable dropdown) filter and a purchase-date range filter —
// Purchase Amount sorting now lives on the Vendor module only.
import * as React from "react";
import { ColumnDef } from "@tanstack/react-table";
import { MoreVertical, X } from "lucide-react";
import moment from "moment";
import { DateRange } from "react-day-picker";
import { DataTable } from "../../shared/data-table";
import { Button } from "ui/button";
import { Combobox } from "ui/combobox";
import { DateRangePicker } from "../Reports/DateRangePicker";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "ui/select";
import AppConstant from "../../Globals/AppConstants";

const SORT_OPTIONS = [
    { id: "0", name: "Latest" },
    { id: "3", name: "Oldest" },
];

export interface VendorPurchaseTableProps {
    data: any[];
    loading: boolean;
    onEdit: (item: any) => void;
    onDelete: (item: any) => void;
    onBillAttachment: (item: any) => void;
    onBillAttachmentSheet: (item: any) => void;
    vendorOptions: { value: string; label: string }[];
    vendorFilter?: string;
    onVendorFilterChange: (vendorId: string) => void;
    dateRange?: DateRange;
    onDateRangeChange: (range: DateRange | undefined) => void;
    sortBy?: any;
    onSortChange: (sortBy: string) => void;
    title?: React.ReactNode;
    headerActions?: React.ReactNode;
    pagination: { page: number; total: number; onChange: (page: number) => void };
}

export function VendorPurchaseTable({
    data,
    loading,
    onEdit,
    onDelete,
    onBillAttachment,
    onBillAttachmentSheet,
    vendorOptions,
    vendorFilter,
    onVendorFilterChange,
    dateRange,
    onDateRangeChange,
    sortBy,
    onSortChange,
    title,
    headerActions,
    pagination,
}: VendorPurchaseTableProps) {
    const columns = React.useMemo<ColumnDef<any, any>[]>(
        () => [
            {
                id: "invoiceId",
                header: "Invoice ID",
                cell: ({ row }) => row.original?.invoiceId,
                meta: { sticky: "left" },
            },
            { id: "vendorName", header: "Vendor Name", cell: ({ row }) => row.original?.vendorName },
            {
                id: "purchaseDate",
                header: "Purchase Date",
                cell: ({ row }) => (row.original?.purchaseDate ? moment(row.original?.purchaseDate).format("DD-MMM-YYYY") : "-"),
            },
            {
                id: "gpayAmount",
                header: "GPay",
                cell: ({ row }) => <span className="tabular-nums">₹{Number(row.original?.gpayAmount ?? 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}</span>,
            },
            {
                id: "cashAmount",
                header: "Cash",
                cell: ({ row }) => <span className="tabular-nums">₹{Number(row.original?.cashAmount ?? 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}</span>,
            },
            {
                id: "shippingCharges",
                header: "Shipping Charges",
                cell: ({ row }) => <span className="tabular-nums">₹{Number(row.original?.shippingCharges ?? 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}</span>,
            },
            {
                id: "totalAmount",
                header: "Total Purchase Amount",
                cell: ({ row }) => <span className="tabular-nums font-medium">₹{Number(row.original?.totalAmount ?? 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}</span>,
            },
            {
                id: "createdOn",
                header: "Created On",
                cell: ({ row }) => (row.original?.createdOn ? moment(row.original?.createdOn).format("DD-MMM-YYYY, hh:mm A") : "-"),
            },
            { id: "createdBy", header: "Created By", cell: ({ row }) => row.original?.createdBy },
            {
                id: "billAttachment",
                header: "Bill Attachment",
                cell: ({ row }) => Number(row.original?.attachmentCount) > 0 ? (
                    <button
                        type="button"
                        onClick={() => onBillAttachment(row.original)}
                        className="text-primary hover:underline"
                    >
                        {AppConstant?.view}
                    </button>
                ) : "-",
            },
            {
                id: "action",
                header: "Action",
                cell: ({ row }) => {
                    const item = row.original;
                    return (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" aria-label="Row actions">
                                    <MoreVertical className="h-4 w-4" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => onEdit(item)}>{AppConstant?.edit}</DropdownMenuItem>
                                <DropdownMenuItem onClick={() => onBillAttachmentSheet(item)}>Bill Attachment</DropdownMenuItem>
                                <DropdownMenuItem className="text-destructive" onClick={() => onDelete(item)}>Delete</DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    );
                },
                meta: { sticky: "right" },
            },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const filterToolbar = (
        <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1">
                <Combobox
                    options={vendorOptions}
                    value={vendorFilter || undefined}
                    onValueChange={onVendorFilterChange}
                    placeholder="Filter by vendor"
                    searchPlaceholder="Search vendor..."
                    className="w-[200px]"
                />
                {vendorFilter && (
                    <Button
                        variant="ghost"
                        size="icon"
                        aria-label="Clear vendor filter"
                        onClick={() => onVendorFilterChange("")}
                    >
                        <X className="h-4 w-4" />
                    </Button>
                )}
            </div>

            <DateRangePicker value={dateRange} onChange={onDateRangeChange} disableFuture placeholder="Purchase Date" />

            <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Sort</span>
                <Select value={sortBy != null ? String(sortBy) : "0"} onValueChange={(v) => onSortChange(v)}>
                    <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        {SORT_OPTIONS.map((o) => (
                            <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>
        </div>
    );

    return (
        <DataTable
            columns={columns}
            data={data || []}
            loading={loading}
            toolbar={filterToolbar}
            title={title}
            headerActions={headerActions}
            pagination={pagination}
        />
    );
}

export default VendorPurchaseTable;
